import { FC, useCallback } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import {
  Option,
  ProgressButton,
} from '@beeline/design-system-react/extracted-styles';

import { EmployeePermission } from 'src/api/permissions';
import { Loader } from 'src/components/Loader/Loader';
import { Panel } from 'src/components/Panel/Panel';
import {
  getCurrentRoom,
  getRoomsDateError,
  getRoomsTimeError,
} from 'src/store/rooms/selectors';
import { getUserPermissions } from 'src/store/user/selectors';
import { Room } from 'src/types';

import { RoomBookingForm } from './RoomBookingForm';
import { RoomBookingSidesheetFields } from './RoomBookingSidesheetFields';
import classes from './RoomBookingSidesheetPanel.module.scss';

export interface RoomBookingFormValues {
  bookedFor: Option<string> | null;
  time: string | undefined;
  tableInterval: Option<string>;
}

interface ActionsProps {
  employeeNumber: string;
  permissions: Set<EmployeePermission>;
}

const Actions: FC<ActionsProps> = ({ employeeNumber, permissions }) => {
  const { submit } = useForm();
  const dateError = useSelector(getRoomsDateError);
  const timeError = useSelector(getRoomsTimeError);

  const bookedFor = useField<Option<string> | null>('bookedFor').input.value;

  const book = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');
  }, [submit]);

  if (!permissions.has(EmployeePermission.BookRooms)) return null;

  return (
    <ProgressButton
      variant='contained'
      onClick={book}
      fullWidth
      disabled={!!(dateError || timeError)}
      data-testid='bookRoom'
    >
      {bookedFor && bookedFor.id !== employeeNumber
        ? 'Забронировать на коллегу'
        : 'Забронировать'}
    </ProgressButton>
  );
};

interface InnerProps {
  room: Room;
  employeeNumber: string;
  permissions: Set<EmployeePermission>;
}

const Inner: FC<InnerProps> = ({ room, employeeNumber, permissions }) => {
  return (
    <RoomBookingForm room={room}>
      <Panel
        actions={
          <Actions employeeNumber={employeeNumber} permissions={permissions} />
        }
        className={classes.panel}
      >
        <RoomBookingSidesheetFields room={room} permissions={permissions} />
      </Panel>
    </RoomBookingForm>
  );
};

export const RoomBookingSidesheetPanel: FC = () => {
  const room = useSelector(getCurrentRoom);
  const data = useSelector(getUserPermissions);

  if (!data?.employeeNumber) return null;

  if (!room) return <Loader />;

  return (
    <Inner
      room={room}
      employeeNumber={data.employeeNumber}
      permissions={data.permissions}
    />
  );
};
